import { useCallback } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Dropdown, Group, IconThreeDotsVer, Typography, useDropdownState, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { RenderIf } from 'components/render-if/RenderIf';
import { useAlert } from 'contexts/alert/useAlert';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useGroup } from 'contexts/group/useGroup';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { ModuleContainer, PageModule, StructureGroupConfig, UiModuleType } from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { AddModuleButton } from 'features/section-page-edit/components/add-module-button/AddModuleButton';
import {
  canRemoveModuleFromGroup,
  getEffectiveItemLimits,
  getNumTotalItemsBeforeIndex,
  getTreatmentType,
  isFirstModuleInGroup
} from 'utils/modules';
import { ModuleItemCount } from './components/module-item-count/ModuleItemCount';
import { ModuleRibbonIndicator } from './components/module-ribbon-indicator/ModuleRibbonIndicator';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { TreatmentTypeDropdown } from './components/treatment-type-dropdown/TreatmentTypeDropdown';
import { UiModuleFieldsHeader } from './components/ui-module-fields-header/UiModuleFieldsHeader';
import styles from './Module.module.scss';

import { DataModelType, EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface ModuleProps extends CommonEntityProps {
  data: ModuleContainer;
}

export const Module = ({
  data: module,
  parentHierarchyId,
  index,
  isHistory,
  groupId,
  isReadOnly = false
}: ModuleProps) => {
  const {
    type,
    generateHierarchyId,
    renderEntity,
    readHierarchyId,
    isReadOnly: contextIsReadOnly,
    getGroupConfig
  } = useDataModelContext();
  const { renderActions: renderMenuActions, hasRenderActions: hasMenuActions } = useContextMenuActions();
  const { group: currentGroup } = useGroup();
  const hierarchyId = generateHierarchyId(module, parentHierarchyId, index);
  const isModuleReadOnly = isReadOnly || contextIsReadOnly;

  const { moduleHasValidationError } = useAlert();
  const { open, setOpen, componentRef } = useDropdownState();

  const handleDropdownClick = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const {
    getTreatmentTypeSetting,
    currentLayout,
    addableUiModules,
    pageTypeSetting,
    addedModulesByTreatmentType,
    moduleInfoByTreatmentType
  } = usePageTypeSettingsContext();

  const pageModule = module.attributes.pageModule as PageModule;
  const treatmentTypeSetting = getTreatmentTypeSetting(pageModule);
  const treatmentType = getTreatmentType(pageModule);
  const {
    min: effectiveMin,
    max: effectiveMax,
    hasLimitOverride
  } = getEffectiveItemLimits(pageModule, treatmentTypeSetting);

  // SETTINGS_ONLY(structure-migration): settingsGroupConfig comes from hardcoded SETTINGS; remove once all pubs are migrated
  const settingsGroupConfig = currentLayout?.groupConfigs?.find((gcfg) => gcfg.id === groupId);
  // END SETTINGS_ONLY(structure-migration)
  const structureGroupConfig = getGroupConfig(groupId ?? '') as StructureGroupConfig | undefined;
  // structureGroupConfig (structure in DynamoDB — new data shape) takes priority.
  // settingsGroupConfig (hardcoded SETTINGS) is the fallback for pubs not yet migrated.
  const groupConfig = structureGroupConfig ?? settingsGroupConfig;

  const hasValidationError = !isHistory && moduleHasValidationError(hierarchyId);

  const numTotalItemsUsed = getNumTotalItemsBeforeIndex(module, module.collection.length);

  const droppablePrefix = isHistory ? DROPPABLE_ID_PREFIXES.HISTORY : DROPPABLE_ID_PREFIXES.MODULE_ITEMS;
  const actionsType = isHistory ? MenuActionsKeys.HistoryModule : MenuActionsKeys.DraftModule;

  const isAvailableLayoutModuleType = (currentLayout?.availableLayoutModules ?? [])
    .map((m) => m.defaultModule.uiModuleType)
    .includes(pageModule.uiModuleType);

  const isFeedModule = pageModule.uiModuleType === UiModuleType.UiFeedsModuleType;
  const isITP = type === DataModelType.ItpDTO;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isDropDisabled = isHistory || isFeedModule || isModuleReadOnly;

  const itpModuleIndex = readHierarchyId(hierarchyId).find((h) => h.entity === EntityType.Page)?.index;

  const effectiveGroupMax = structureGroupConfig
    ? (structureGroupConfig.max ?? undefined)
    : settingsGroupConfig?.maximumAllowed;
  const hasMaxNumberOfModulesInGroup = !!effectiveGroupMax && currentGroup.collection.length >= effectiveGroupMax;

  const canRemoveFromGroup = canRemoveModuleFromGroup(
    groupConfig
      ? {
          removableTreatmentTypes: groupConfig.removableTreatmentTypes ?? null,
          canToggle: groupConfig.canToggle ?? undefined,
          min: structureGroupConfig?.min ?? undefined,
          minimumAllowed: settingsGroupConfig?.minimumAllowed
        }
      : undefined,
    treatmentType,
    currentGroup.collection.length
  );

  const hasNoModulesToAdd =
    !addableUiModules || (addableUiModules.length === 0 && !currentLayout?.availableTreatmentTypes?.length);

  const isAddModuleButtonVisible =
    index !== undefined &&
    !groupId &&
    isAvailableLayoutModuleType &&
    !isHistory &&
    !isITP &&
    !hasNoModulesToAdd &&
    !isModuleReadOnly;

  const treatmentTypeMessageExists = typeof treatmentTypeSetting?.message === 'string';
  const moduleIsFirstInGroup = !!groupId && isFirstModuleInGroup(module, currentGroup.collection);

  return (
    <Wrapper
      margin={{
        bottom:
          (!isAvailableLayoutModuleType || hasMaxNumberOfModulesInGroup || isHistory || hasNoModulesToAdd) &&
          !groupConfig?.draggableTreatmentTypes?.length
            ? 'mlg'
            : 'none'
      }}
      data-model-hierarchy-id={hierarchyId}
      className={styles.moduleWrapper}
    >
      {moduleIsFirstInGroup && !isHistory && !isITP && !isModuleReadOnly && (
        <AddModuleButton groupId={groupId} nextModuleIndex={index!}/>
      )}
      <Group margin={{ bottom: 'xs' }} align="space-between">
        <Group gap="none" data-testid="page-treatment-header">
          {isITP && typeof itpModuleIndex === 'number' && (
            <Typography size="sm" weight="bold" margin={{ right: 'sm' }} data-testid="page-treatment-type">
              {`Page ${Number(itpModuleIndex) + 1}`}
            </Typography>
          )}
          <TreatmentTypeDropdown
            hierarchyId={hierarchyId}
            module={module}
            pageModule={pageModule}
            pageTypeSetting={pageTypeSetting}
            showCurrentTreatmentTypeAtTheTop
            viewOnly={isHistory}
            isReadOnly={isModuleReadOnly}
          />
        </Group>
        {hasMenuActions(actionsType, {
          entity: module,
          hierarchyId,
          index,
          extraProperties: { numTotalItemsUsed, addedModulesByTreatmentType, moduleInfoByTreatmentType }
        }) && (
          <Dropdown
            open={open}
            padding={{ all: 'none' }}
            position="right"
            trigger={<IconThreeDotsVer color="asphalt" />}
            data-testid="page-draft-ellipsis-icon"
            onToggle={handleDropdownClick}
            componentRef={componentRef}
          >
            {renderMenuActions(actionsType, {
              entity: module,
              hierarchyId,
              index,
              extraProperties: {
                treatmentType: treatmentTypeSetting?.treatmentType ?? '',
                maxAllowedItems: effectiveMax ?? 0,
                numTotalItemsUsed,
                setOpen,
                ignoreBaseQuery: treatmentTypeSetting?.ignoreBaseQuery ?? false,
                canRemoveFromGroup,
                // SETTINGS_ONLY(structure-migration): old-shape pubs mark a whole group as non-removable via
                // settingsGroupConfig.removable=false. Structure pubs don't have that flag — removability is
                // determined per-treatment-type via canRemoveFromGroup (computed from removableTreatmentTypes).
                // forceHideRemoveFromGroup is therefore always false for structure pubs and can be removed once
                // all pubs are migrated.
                forceHideRemoveFromGroup: settingsGroupConfig?.removable === false,
                addedModulesByTreatmentType,
                moduleInfoByTreatmentType,
                isOptionalModule: treatmentTypeSetting?.optional ?? false
              }
            })}
          </Dropdown>
        )}
      </Group>
      <UiModuleFieldsHeader pageModule={pageModule} />
      {module.attributes.ribbonUrl && <ModuleRibbonIndicator ribbonUrl={module.attributes.ribbonUrl} />}
      <Wrapper
        data-testid="page-module-feed-module"
        padding={{ all: 'xs' }}
        className={classnames(
          styles.moduleBox,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          isFeedModule ? styles.moduleFeedsArea : styles.moduleDashedArea
        )}
        data-error={hasValidationError ? 'true' : 'false'}
      >
        <Droppable droppableId={droppablePrefix + hierarchyId} isDropDisabled={isDropDisabled}>
          {(droppableProvided) => (
            <div
              data-testid="page-module-item"
              data-max={effectiveMax}
              className={styles.moduleDroppableArea}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {module.collection.map((item, i) => {
                let availableContentSlots =
                  effectiveMax !== undefined
                    ? effectiveMax - getNumTotalItemsBeforeIndex(module, i)
                    : effectiveMin !== undefined
                      ? effectiveMin - getNumTotalItemsBeforeIndex(module, i)
                      : undefined;
                if (typeof availableContentSlots === 'number' && availableContentSlots < 0) {
                  availableContentSlots = 0;
                }

                const articleHierarchyId = generateHierarchyId(item, hierarchyId, i);
                const articleActionsType = isHistory ? MenuActionsKeys.HistoryArticle : MenuActionsKeys.DraftArticle;

                return (
                  <div key={articleHierarchyId + item.attributes.id}>
                    {renderEntity(item, {
                      hierarchyId,
                      index: i,
                      isHistory,
                      extraProps: {
                        content: {
                          availableContentSlots,
                          isDraft: !isHistory && !isModuleReadOnly,
                          ignoreBaseQuery: treatmentTypeSetting?.ignoreBaseQuery ?? false
                        },
                        article: {
                          renderActions: () =>
                            renderMenuActions(articleActionsType, {
                              hierarchyId: articleHierarchyId,
                              entity: item,
                              index: i
                            }),
                          isOverflow: isITP && i >= (effectiveMax ?? 0)
                        },
                        module: { treatmentTypeSetting }
                      },
                      isReadOnly: isModuleReadOnly
                    })}
                  </div>
                );
              })}
              {droppableProvided.placeholder}
              {!treatmentTypeMessageExists || hasLimitOverride ? (
                <ModuleItemCount
                  min={effectiveMin}
                  max={effectiveMax}
                  showMaxInDenominator
                  count={numTotalItemsUsed}
                  contentTypes={treatmentTypeSetting?.contentTypes}
                  data-testid="page-module-item-count"
                />
              ) : module.collection.length === 0 ? (
                // The drag is not detected if the module is contentless. It requires
                // at least an empty element
                <span>&nbsp;</span>
              ) : null}
            </div>
          )}
        </Droppable>
      </Wrapper>
      <RenderIf condition={!isAddModuleButtonVisible && !treatmentTypeMessageExists}>
        {/* Adds extra space at the bottom of the module if it's read only and there's no message */}
        <Wrapper margin={{ bottom: 'lg' }} />
      </RenderIf>
      <RenderIf condition={treatmentTypeMessageExists && !hasLimitOverride}>
        <Typography
          margin={{ top: 'xs', bottom: isAddModuleButtonVisible ? 'none' : 'md' }}
          size="sm"
          weight="reg"
          color="asphalt"
          className={classnames({
            [styles.fullWidthContainerNoCount]: !groupConfig?.showModuleCount,
            [styles.fullWidthContainerWithCount]: groupConfig?.showModuleCount
          })}
          data-testid="page-module-treatment-message"
        >
          {treatmentTypeSetting?.message}
        </Typography>
      </RenderIf>
      <RenderIf condition={isAddModuleButtonVisible}>
        <AddModuleButton
          addableUiModules={addableUiModules}
          nextModuleIndex={index! + 1}
          parentHierarchyId={parentHierarchyId}
        />
      </RenderIf>
      {/* shown for any grouped module (structure or SETTINGS); mutually exclusive with the button above which requires !groupId */}
      {index !== undefined && groupId && !isHistory && !isITP && !isModuleReadOnly && (
        <AddModuleButton groupId={groupId} nextModuleIndex={index + 1} />
      )}
    </Wrapper>
  );
};
